const en = {
  site: {
    tagline: 'Intentional Leadership Coaching by Tamara Bonilla',
  },

  nav: {
    links: [
      {label: 'About',       href: '#about'},
      {label: 'Methodology', href: '#methodology'},
      {label: 'Services',    href: '#services'},
      {label: 'FAQ',         href: '#faq'},
    ],
    cta: 'Book Discovery Call',
  },

  footer: {
    contactLabel:   'Get in touch',
    copyright:      'All rights reserved.',
    privacyPolicy:  'Privacy Policy',
    termsOfService: 'Terms of Service',
  },

  cookie: {
    messageBefore: 'We use cookies to improve your experience and understand how visitors use our site. Read our',
    policyLink:    'Privacy Policy',
    messageAfter:  'for details.',
    accept:        'Accept',
    decline:       'Decline',
    ariaLabel:     'Cookie consent',
  },

  hero: {
    eyebrow:      'Your Change Mentor',
    headline:     "Most Leadership Training Teaches You to Do More. I'll Help You Do Less of the Wrong Things.",
    subheadline:  "Stop leading from hustle. Start leading from yourself — with a methodology that works at the level of identity, body, and behavior simultaneously.",
    primaryCta:   {label: 'Book a Discovery Call',   anchor: '#lead-capture'},
    secondaryCta: {label: 'Join the Newsletter', anchor: '#lead-capture'},
    trust:        'Join first-time managers across 3 continents learning to lead intentionally',
    cards: [
      {icon: '🧠', title: 'Immunity to Change', sub: "Uncover what's really keeping you stuck"},
      {icon: '✨', title: 'Human Design',        sub: 'Lead as yourself — not the template'},
      {icon: '🌬️', title: 'HypnoBreathwork',    sub: 'Make lasting change at the body level'},
    ],
  },

  problem: {
    eyebrow: 'The Reality',
    heading: 'You Got Promoted. Now What?',
    sub:     "You worked hard to get here. But nobody warned you that the skills that earned your promotion are not the skills that will make you a great leader.",
    stats: [
      {value: '82%', label: 'of managers receive NO formal leadership training before taking their first team',      source: 'Chartered Management Institute'},
      {value: '74%', label: 'of first-time managers struggle to maintain work-life balance in their new role',      source: 'CCL Research'},
      {value: '59%', label: 'cite adjusting to authority and people management as their top challenge',             source: 'Center for Creative Leadership'},
    ],
    empathy: "I know because I've been there. I was told to fake it until I make it. But this made me feel inadequate, and my impostor syndrome was running the show. Is not about faking it, it is about \"failing until you learn it\" and THAT's why this work exists, to help you navigate that transition courageously",
    beliefs: [
      {belief: '"I need to have all the answers to be a good leader."',    truth: 'Great leaders ask great questions. Answers can be outsourced — judgment cannot.'},
      {belief: '"If I show vulnerability, they won\'t respect me."',        truth: 'Clarity and honesty ARE authority. Performed confidence erodes it over time.'},
      {belief: '"I don\'t have time to rest or do what I love."',           truth: 'Rest is not the enemy of productivity. It is the strategy.'},
      {belief: '"Leadership means being perfect all the time."',            truth: 'Perfection is armor. Intentional leadership is the work underneath it.'},
    ],
    bridge:         "There's a different way to lead.",
    beliefsHeading: 'The stories keeping you stuck:',
    seeMethod:      'See the Method →',
  },

  about: {
    eyebrow: 'About Tamara',
    name:    'Tamara Bonilla',
    title:   'Change Catalyst, Leadership Enthusiast, Human Design Coach, Hypnobreathwork Facilitator',
    bio: [
      "I built a career in corporate compliance, and became the person everyone called the high-performer — until I hit a wall I couldn't push through with more hustle.",
      "I was leading from fear, from perfectionism, from the exhausting belief that if I just worked harder and showed no weakness, I'd finally feel like I belonged in the room. It wasn't sustainable. And it wasn't me.",
      "The pivot wasn't a single moment. It was an accumulation of tools — Human Design, which gave me permission to lead as myself instead of as the template. The Immunity to Change framework, which revealed the unconscious commitments keeping me stuck. HypnoBreathwork, which helped my nervous system actually believe the changes my mind had made.",
      "Today I work with first-time leaders who are doing what I did — performing leadership instead of developing it. My gift, as a Human Design Projector, is seeing people's wholeness before they can see it themselves. I help them stop faking it and start building it.",
    ],
    certifications: [
      'Certified Human Design Analyst',
      'HypnoBreathwork Practitioner',
      'Immunity to Change Map Facilitator',
      'Prosci Certified Change Practitioner',
    ],
    workWithCta: 'Work With Tamara',
  },

  methodology: {
    eyebrow: 'The YCM Method',
    heading: 'Three Steps. Three Levels. One Transformation.',
    sub:     "Most programs address what you're doing wrong. YCM addresses why you keep doing it — and gives you tools to change at every level: cognitive, subconscious, and somatic.",
    steps: [
      {
        number:  '01',
        label:   'Immunity to Change (Harvard ITC)',
        accent:  'teal',
        heading: 'Awareness Mapping',
        problem: "You keep trying the same strategies and wondering why you can't break through — despite your best efforts.",
        description: 'The Immunity to Change framework reveals the hidden psychological forces keeping even high-performing leaders stuck. Without understanding your internal resistance system, you keep sabotaging your own progress.',
        learn: [
          "Identify your authentic improvement goal — what truly matters to you AND those around you",
          "Map the behaviors you're doing (or not doing) that contradict your stated commitments",
          "Uncover the big assumptions driving your protective behaviors",
        ],
        milestone: "You complete your Immunity to Change map and experience the 'aha moment' — finally understanding why change has felt so hard, despite wanting it so badly.",
      },
      {
        number:  '02',
        label:   'Human Design',
        accent:  'lavender',
        heading: 'Authentic Design',
        problem: "You burn out trying to follow leadership advice that doesn't align with your natural energy. You feel inauthentic and drain yourself trying to be someone you're not.",
        description: 'Human Design reveals how you are specifically designed to make decisions, interact with others, and sustain your energy. When you operate according to your design, you stop fighting yourself.',
        learn: [
          "Your leadership style based on your energy type and Human Design profile",
          "Your unique decision-making strategy (Authority) — so decisions stop feeling agonizing",
          "Your energy patterns and sustainable work rhythms — no more one-size-fits-all advice",
        ],
        milestone: "You have a personalized Leadership Design Report and have implemented design-aligned strategies that noticeably increase your energy and effectiveness.",
      },
      {
        number:  '03',
        label:   'HypnoBreathwork',
        accent:  'accent',
        heading: 'Nervous System Integration',
        problem: "You intellectually understand what you need to change but can't consistently embody it — especially under stress. You revert to old patterns when pressure hits.",
        description: 'Lasting change happens at the nervous system level, not just the cognitive level. HypnoBreathwork fuses conscious breathing, hypnosis, and neuroscience-backed visualization to reach the subconscious in a matter of minutes, not hours.',
        learn: [
          "The connection between your nervous system state, emotional intelligence, and leadership effectiveness",
          "HypnoBreathwork — reaches theta state in a matter of minutes, not hours, vs. traditional meditation",
          "Pre-meeting and pre-decision regulation routines you can use immediately",
        ],
        milestone: "You can quickly shift your nervous system state when stressed, maintain calm presence during difficult leadership situations, and have established daily practices that sustain high performance without burnout.",
      },
    ],
    learnTitle: "What you'll learn:",
    startCta:   'Start Your Transformation',
  },

  servicesSection: {
    label:    'How I Work With You',
    heading:  'Choose Your Path',
    sub:      "Every engagement is built around your specific context — whether you're an individual leader, a team, or an organization.",
    whoLabel: 'Who:',
  },

  services: [
    {
      id:      '1on1-coaching',
      title:   '1:1 Leadership Coaching & Mentorship',
      who:     'First-time managers navigating the transition from individual contributor to intentional leader — in their corporate role, not away from it.',
      format:  'Private sessions online, flexible scheduling',
      outcome: "From imposter syndrome and hustle to confident, grounded leadership — without burning out or becoming someone you're not.",
      cta:     {label: 'Book a Discovery Call', anchor: '#lead-capture'},
      icon:    'coaching',
    },
    {
      id:      'group-coaching',
      title:   'Group Coaching & Breathwork Circles',
      who:     'Leaders ready for peer-supported transformation in a curated group setting.',
      format:  'Live cohort program or ongoing group circles (online)',
      outcome: "Build your leadership identity alongside others doing the same work — accountability, connection, and the relief of knowing you're not alone.",
      cta:     {label: 'Join the Waitlist', href: 'mailto:hello@yourchangementor.com?subject=Group Coaching Waitlist'},
      icon:    'group',
    },
    {
      id:      'corporate-breathwork',
      title:   'Corporate Workshops',
      who:     'HR leaders, L&D teams, and team managers seeking experiential wellbeing interventions.',
      format:  'In-person or virtual facilitated breathwork sessions - team offsites, team building, retreats.',
      outcome: "Reset team stress, improve decision-making clarity, build psychological safety — in a single session.",
      cta:     {label: 'Request a Corporate Session', href: 'mailto:hello@yourchangementor.com?subject=Corporate Session Request'},
      icon:    'corporate',
    },
    {
      id:      'speaking',
      title:   'Events & Speaking Facilitation',
      who:     'Conference organizers, leadership summits, corporate events, retreat facilitators.',
      format:  'Keynote, panel, workshop, or full breathwork experience.',
      outcome: "Your audience leaves with a transformational experience AND practical tools they can use the next morning.",
      cta:     {label: 'Book Tamara for Your Event', href: 'mailto:hello@yourchangementor.com?subject=Speaking Inquiry'},
      icon:    'speaking',
    },
  ],

  results: {
    eyebrow: 'Social Proof',
    heading: 'Transformation That Sticks',
    sub:     'Real outcomes from leaders who committed to leading differently.',
    metrics: [
      {value: 200, suffix: '+',    label: 'Leaders Impacted'},
      {value: 3,   suffix: '',     label: 'Continents Served'},
      {value: 4,   suffix: ' min', label: 'to reach theta state with HypnoBreathwork'},
      {value: 95,  suffix: '%',    label: 'report lasting behavior change within 8 weeks'},
    ],
    testimonials: [
      {
        quote: "I spent my first year as a manager completely exhausted, trying to be everything to everyone. Working with Tamara, I finally understood WHY I kept self-sabotaging — and more importantly, how to stop. The Human Design work alone was worth it.",
        name:  'Engineering Lead, Series B Startup',
      },
      {
        quote: "I went from dreading difficult conversations to actually feeling ready for them. The breathwork gives me something I can use IN THE MOMENT, not just in theory. This isn't like any coaching I've tried before.",
        name:  'Senior Manager, Global Bank',
      },
    ],
  },

  leadMagnet: {
    eyebrow:   'Free Newsletter',
    heading:   'Leadership Lab',
    subtitle:  'Monthly Insights for First-Time Managers',
    sub:       "Practical leadership insights, mindset shifts, and real-world tools — delivered monthly on LinkedIn. Written for first-time managers navigating the hardest transition of their careers.",
    bullets: [
      'Immunity to Change insights for everyday leadership',
      'Human Design tips for your leadership style',
      'HypnoBreathwork tools you can use before your next meeting',
    ],
    guideLabel:        'Newsletter',
    tabGuide:          'Join the Newsletter',
    tabBooking:        'Book a Call',
    newsletterHeading: 'Join the Leadership Lab on LinkedIn',
    newsletterText:    "Monthly insights for first-time managers — practical tools, mindset shifts, and leadership frameworks. Delivered free, straight to your LinkedIn feed.",
    cta:               'Subscribe on LinkedIn',
    bookingHeading:    'Book a Free 30-min Discovery Call',
    bookingText:       "Let's talk about where you are and where you want to go. No pitch — just a real conversation.",
    bookingCta:        'Book Discovery Call',
  },

  podcast: {
    eyebrow:      'Featured On',
    heading:      'Tamara on The Mastry Experience',
    episodeTitle: 'The Achiever Trap: Productivity, Burnout & Nervous System Healing',
    podcastName:  'The Mastry Experience Podcast',
    description:  "Tamara joins The Mastry Experience to unpack what happens when high-achievement becomes a trap — and how nervous system healing and a redefined relationship with productivity unlock sustainable leadership.",
    watchCta:     'Watch on YouTube',
    watchUrl:     'https://youtu.be/i-ynRy_0NUQ',
  },

  faqSection: {
    label:              'FAQ',
    heading:            'Common Questions',
    sub:                'Everything you need to know before deciding if YCM is right for you.',
    stillHaveQuestions: 'Still have questions?',
    emailUs:            'Email Us Directly',
  },

  faq: [
    {
      q: "What is Human Design and do I need to know anything about it?",
      a: "No prior knowledge needed. Human Design is your leadership operating manual — a personalized map based on your birth data that reveals how your energy works, how you make best decisions, and how you naturally lead. Tamara guides you through the entire process. Think of it as the instruction manual you never got.",
    },
    {
      q: "I've tried coaching before and it didn't stick. What's different here?",
      a: "Most coaching works only at the mindset level. YCM works at three levels simultaneously: identity (Human Design), subconscious and body (HypnoBreathwork), and behavior (Immunity to Change). Change sticks when all three levels are addressed together — which is why most standalone programs don't create lasting results.",
    },
    {
      q: "What exactly is HypnoBreathwork? Is it like meditation?",
      a: "It's faster and more accessible than traditional meditation. HypnoBreathwork reaches theta state — deep subconscious access — in about 4 minutes, vs. 15+ minutes for traditional meditation. The active nature of breath gives your mind an anchor, so there's no 'I can't stop thinking' frustration. It fuses breathwork, hypnosis, and visualization into one practice. No prior experience required.",
    },
    {
      q: "Is this only for people who want to leave corporate?",
      a: "Not at all. YCM is designed specifically for leaders who want to STAY in their corporate roles and lead better — with more ease, confidence, and sustainability. The transformation happens from the inside while you remain exactly where you are. Many clients say the work makes their corporate career feel livable again.",
    },
    {
      q: "I'm swamped as a new manager. How much time does this actually require?",
      a: "Coaching is designed for busy professionals. Sessions are flexible and online. The tools you learn — breathwork routines, pre-meeting regulation practices, design-aligned decisions — integrate into your existing schedule, not on top of it. Most clients spend 60–90 minutes per week on their YCM work.",
    },
    {
      q: "Do you work with teams or only individuals?",
      a: "Both. 1:1 coaching and group programs serve individual leaders; corporate breathwork sessions and speaking engagements serve teams and organizations of all sizes. If you're an HR or L&D leader exploring team programs, reach out directly.",
    },
    {
      q: "Do you offer sessions in Spanish?",
      a: "Yes. Tamara works with both English- and Spanish-speaking clients and delivers corporate sessions and email content in both languages. The YCM email nurture sequence is available in Spanish through Flodesk.",
    },
    {
      q: "What results can I realistically expect?",
      a: "Clients consistently report clearer decision-making, reduced anxiety and reactivity, stronger team relationships, and more confidence in difficult conversations — within the first few weeks. The methodology is designed for lasting change, not temporary relief. Transformation doesn't get easier. You get better at choosing which discomfort moves you forward.",
    },
  ],

  whyNow: {
    label:   'Why Now',
    heading: 'Why Leaders Are Seeking Support Now',
    points: [
      {title: 'The pace of change is accelerating',         body: 'Organizations are restructuring faster than ever. Leaders who can navigate ambiguity have a compounding advantage.'},
      {title: 'Career inflection points are higher stakes', body: 'The first leadership role sets the trajectory. Patterns formed now — good or bad — become self-reinforcing.'},
      {title: 'Frameworks outlast any single project',      body: 'The tools you build here follow you across roles, companies, and industries. This is career infrastructure.'},
    ],
    visualTitle: 'The cost of not acting',
    visualItems: [
      'Team disengagement during change',
      'Career stagnation at a critical inflection',
      'Burnout from leading without a system',
      'Missing the window for your next move',
    ],
    ctaText:    'Ready to lead change on your terms?',
    bookingCta: 'Book Discovery Call',
  },

  legal: {
    privacy: {
      pageLabel:     'Legal',
      title:         'Privacy Policy',
      updatedPrefix: 'Last updated:',
      intro:         'Your Change Mentor ("Your Change Mentor", "we", "us", or "our") is committed to protecting your personal information. This Privacy Policy explains what we collect, how we use it, and your rights regarding your data.',

      s1Heading:   '1. Information We Collect',
      s1Para1:     'We collect information you provide directly to us, including:',
      s1Item1Bold: 'Contact information:',
      s1Item1Text: 'Name, email address, company name, and optionally phone number when you submit our lead capture form.',
      s1Item2Bold: 'Communications:',
      s1Item2Text: 'Messages you send to us by email or through the site.',
      s1Item3Bold: 'Booking information:',
      s1Item3Text: 'If you book a discovery call via Calendly, Calendly collects your name, email, and scheduling preferences per their own privacy policy.',
      s1Para2:     'We may also collect information automatically, including:',
      s1AutoItem:  'Browser type, operating system, referring URLs, and pages visited (via cookies or analytics tools when enabled).',

      s2Heading: '2. How We Use Your Information',
      s2Para1:   'We use the information we collect to:',
      s2Items: [
        'Deliver the free resource you requested via email.',
        'Respond to your inquiries and schedule discovery calls.',
        'Send relevant coaching and mentoring communications (you may opt out at any time).',
        'Improve our site and services.',
      ],
      s2Para2: 'We do not sell your personal information to third parties.',

      s3Heading:   '3. How We Share Your Information',
      s3Para1:     'We may share your information with:',
      s3Item1Bold: 'Email service providers',
      s3Item1Text: '(Mailchimp or SendGrid) to deliver your requested resource and send follow-up communications.',
      s3Item2Bold: 'Calendly',
      s3Item2Text: 'to facilitate discovery call bookings.',
      s3Item3Bold: 'Google Sheets / Zapier or Make',
      s3Item3Text: 'for internal lead management.',
      s3Item4Bold: 'Cloudflare',
      s3Item4Text: 'for site hosting, security, and CDN services.',
      s3Para2:     'All third-party providers are required to handle your data securely and in accordance with their own privacy policies.',

      s4Heading: '4. Cookies',
      s4Para1:   'We use cookies to remember your cookie consent preference. When analytics tools are enabled in a future update, cookies may also be used to analyze site traffic. You can control cookie preferences through your browser settings. Our cookie consent banner gives you the option to accept or decline non-essential cookies.',

      s5Heading: '5. Data Retention',
      s5Para1:   'We retain your personal information for as long as needed to deliver our services and fulfill the purposes described in this policy. You may request deletion of your data at any time.',

      s6Heading:    '6. Your Rights',
      s6Para1:      'Depending on your location, you may have the right to:',
      s6Items: [
        'Access the personal data we hold about you.',
        'Request correction of inaccurate data.',
        'Request deletion of your data.',
        'Opt out of marketing communications at any time.',
      ],
      s6EmailBefore: 'To exercise any of these rights, email us at',
      s6EmailAfter:  'with the subject line "Privacy Request." We will respond within 30 days.',

      s7Heading: '7. Security',
      s7Para1:   'We use industry-standard security measures to protect your personal information. However, no method of transmission over the internet is 100% secure. We cannot guarantee absolute security.',

      s8Heading: "8. Children's Privacy",
      s8Para1:   'Our services are not directed to individuals under the age of 16. We do not knowingly collect personal information from children.',

      s9Heading: '9. Changes to This Policy',
      s9Para1:   'We may update this Privacy Policy from time to time. We will notify you of significant changes by updating the date at the top of this page. Continued use of the site after changes constitutes your acceptance of the updated policy.',

      s10Heading: '10. Contact Us',
      s10Para1:   'If you have questions about this Privacy Policy or how we handle your data, contact us at:',
    },

    terms: {
      pageLabel:     'Legal',
      title:         'Terms of Service',
      updatedPrefix: 'Last updated:',
      intro:         'Please read these Terms of Service ("Terms") carefully before using the yourchangementor.com website (the "Site") operated by Your Change Mentor ("we", "us", or "our"). By accessing or using the Site, you agree to be bound by these Terms.',

      s1Heading: '1. Use of the Site',
      s1Para1:   'You may use the Site for lawful purposes only. You agree not to use the Site in any way that:',
      s1Items: [
        'Violates any applicable local, national, or international law or regulation.',
        'Is harmful, fraudulent, deceptive, or misleading.',
        'Infringes the rights of any third party.',
        'Attempts to gain unauthorized access to any part of the Site or its infrastructure.',
      ],

      s2Heading: '2. Intellectual Property',
      s2Para1:   'All content on this Site — including text, graphics, logos, downloadable resources, and the overall design — is the property of Your Change Mentor and protected by applicable intellectual property laws. You may not reproduce, distribute, or create derivative works without our express written permission.',
      s2Para2:   'The free resources (Change Readiness Self-Assessment and Career Transition Roadmap Guide) are provided for your personal, non-commercial use only.',

      s3Heading: '3. Coaching & Mentoring Services',
      s3Para1:   'Any coaching or mentoring services described on this Site are subject to a separate service agreement. Content on this Site is for informational purposes only and does not constitute professional advice.',
      s3Para2:   'Results described or implied on this Site represent outcomes achieved by specific individuals and are not guaranteed. Individual results will vary.',

      s4Heading: '4. Disclaimer of Warranties',
      s4Para1:   'The Site and its content are provided "as is" without warranties of any kind, either express or implied, including but not limited to implied warranties of merchantability, fitness for a particular purpose, or non-infringement.',

      s5Heading: '5. Limitation of Liability',
      s5Para1:   'To the fullest extent permitted by law, Your Change Mentor shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of — or inability to use — this Site or its content.',

      s6Heading: '6. Third-Party Links & Services',
      s6Para1:   'This Site may contain links to third-party websites or integrate third-party services (such as Calendly for booking). We are not responsible for the content, privacy practices, or terms of those third parties. Use them at your own discretion.',

      s7Heading: '7. Governing Law',
      s7Para1:   'These Terms are governed by and construed in accordance with applicable law. Any dispute arising from these Terms shall be resolved in the jurisdiction where Your Change Mentor operates.',

      s8Heading: '8. Changes to These Terms',
      s8Para1:   'We reserve the right to update these Terms at any time. We will indicate the date of the most recent update at the top of this page. Continued use of the Site after changes are posted constitutes acceptance of the revised Terms.',

      s9Heading: '9. Contact Us',
      s9Para1:   'If you have questions about these Terms, contact us at:',
    },
  },
};

export default en;
